import { Helmet } from "react-helmet-async";
import { SITE_URL, SITE_BRAND, DEFAULT_OG_IMAGE } from "@/lib/seo-config";

/**
 * <SEO /> — Composant SEO ultra-optimisé pour Batisur Connect
 * Injecte : title, description, keywords, canonical, Open Graph, Twitter Cards, JSON-LD
 *
 * Usage : <SEO cfg={getSEO('/pour-qui')} />
 * ou     <SEO title="..." description="..." keywords="..." canonical="/xxx" schema={[...]} />
 */
export default function SEO({ cfg, title, description, keywords, canonical, ogType, ogImage, schema, noindex }) {
  const t = title || cfg?.title || `${SITE_BRAND} · Marketplace BTP sécurisée`;
  const d = description || cfg?.description || "Batisur Connect — plateforme française sécurisée pour travaux BTP.";
  const k = keywords || cfg?.keywords || "";
  const c = canonical || cfg?.canonical || "/";
  const canonicalUrl = c.startsWith("http") ? c : `${SITE_URL}${c}`;
  const ogT = ogType || cfg?.ogType || "website";
  const ogI = ogImage || cfg?.ogImage || DEFAULT_OG_IMAGE;
  const schemas = schema || cfg?.schema || [];
  const noIdx = noindex ?? cfg?.noindex ?? false;

  return (
    <Helmet>
      {/* Basiques */}
      <title>{t}</title>
      <meta name="description" content={d} />
      {k && <meta name="keywords" content={k} />}
      <link rel="canonical" href={canonicalUrl} />
      {noIdx ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      <meta name="language" content="fr-FR" />
      <meta name="author" content={SITE_BRAND} />
      <meta name="geo.region" content="FR" />
      <meta name="geo.placename" content="France" />

      {/* Open Graph (Facebook, LinkedIn, WhatsApp preview) */}
      <meta property="og:type" content={ogT} />
      <meta property="og:site_name" content={SITE_BRAND} />
      <meta property="og:title" content={t} />
      <meta property="og:description" content={d} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogI} />
      <meta property="og:image:alt" content={t} />
      <meta property="og:locale" content="fr_FR" />

      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={t} />
      <meta name="twitter:description" content={d} />
      <meta name="twitter:image" content={ogI} />

      {/* JSON-LD structured data (Schema.org) — résultats enrichis Google */}
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}
